/* ============================================================
 * <lion-image-comparison> — interactive before/after slider
 * Touch, mouse, and keyboard accessible (ArrowLeft/Right).
 * ============================================================ */

class ImageComparison extends HTMLElement {
  constructor() {
    super();
    this.frame = this.querySelector('.image-comparison__frame');
    this.divider = this.querySelector('[data-divider]');
    this.handle = this.querySelector('[data-handle]');
    this.secondLayer = this.querySelector('[data-second-layer]');
    this.isDragging = false;

    this._initial = parseFloat(this.dataset.initial || 50);
    this._setPosition(this._initial);

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this.handle.addEventListener('mousedown', this._onPointerDown);
    this.handle.addEventListener('touchstart', this._onPointerDown, { passive: false });
    this.handle.addEventListener('keydown', this._onKeyDown);

    // Allow clicking anywhere on the frame to jump
    this.frame.addEventListener('click', (e) => {
      if (e.target.closest('[data-handle]')) return;
      this._updateFromClientX(e.clientX);
    });
  }

  _onPointerDown(e) {
    e.preventDefault();
    this.isDragging = true;
    this.handle.setAttribute('aria-grabbed', 'true');
    document.addEventListener('mousemove', this._onPointerMove);
    document.addEventListener('mouseup', this._onPointerUp);
    document.addEventListener('touchmove', this._onPointerMove, { passive: false });
    document.addEventListener('touchend', this._onPointerUp);
  }

  _onPointerMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    this._updateFromClientX(clientX);
  }

  _onPointerUp() {
    this.isDragging = false;
    this.handle.removeAttribute('aria-grabbed');
    document.removeEventListener('mousemove', this._onPointerMove);
    document.removeEventListener('mouseup', this._onPointerUp);
    document.removeEventListener('touchmove', this._onPointerMove);
    document.removeEventListener('touchend', this._onPointerUp);
  }

  _onKeyDown(e) {
    const step = e.shiftKey ? 10 : 2;
    let current = parseFloat(this.handle.style.left || this._initial);
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._setPosition(Math.max(0, current - step));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._setPosition(Math.min(100, current + step));
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._setPosition(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      this._setPosition(100);
    }
  }

  _updateFromClientX(clientX) {
    const rect = this.frame.getBoundingClientRect();
    const x = clientX - rect.left;
    let pct = (x / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    this._setPosition(pct);
  }

  _setPosition(pct) {
    if (!this.divider || !this.secondLayer) return;
    this.divider.style.left = pct + '%';
    this.secondLayer.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
    this.handle.style.left = pct + '%';
    this.handle.setAttribute('aria-valuenow', Math.round(pct));
  }
}

if (!customElements.get('lion-image-comparison')) {
  customElements.define('lion-image-comparison', ImageComparison);
}
